// Ma Boutique - Personnalisation Espace Vendeur
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Store, Image, Palette, Globe, Share2, Eye, Edit, Save,
  Facebook, Instagram, Twitter, Linkedin, Youtube, MapPin, Phone, Mail,
  Upload, Camera, CheckCircle2, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Section avec titre
function Section({ title, description, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <div className="mb-6">
        <h2 className="font-bold text-lg text-gray-900">{title}</h2>
        {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
      </div>
      {children}
    </div>
  );
}

export default function MaBoutiquePage() {
  const [loading, setLoading] = useState(false);
  const [shop, setShop] = useState({
    name: 'Outillage Pro Plus',
    slug: 'outillage-pro-plus',
    description: 'Spécialiste de l\'outillage professionnel depuis 2015. Nous proposons une large gamme d\'outils de qualité pour les artisans et les bricoleurs exigeants.',
    logo: null,
    banner: null,
    primaryColor: '#F97316',
    address: '12 rue de la Paix, 75002 Paris',
    phone: '01 23 45 67 89',
    email: 'contact@outillage-pro-plus.fr',
    website: 'https://outillage-pro-plus.fr',
    socials: {
      facebook: 'https://facebook.com/outillageproplus',
      instagram: 'https://instagram.com/outillageproplus',
      twitter: '',
      linkedin: '',
      youtube: ''
    },
    openingHours: 'Lun-Ven: 9h-18h, Sam: 9h-12h',
    verified: true
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simuler la sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Boutique mise à jour avec succès');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (type) => {
    // Simuler l'upload d'image
    toast.info(`Upload ${type} - Fonctionnalité à implémenter`);
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="ma-boutique-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Ma Boutique</h1>
          <p className="text-gray-500">Personnalise l'apparence de ta boutique</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.open(`/boutique/${shop.slug}`, '_blank')}
            className="rounded-xl"
          >
            <Eye size={16} className="mr-2" />
            Aperçu
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 rounded-xl"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Save size={16} className="mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Preview Card */}
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
            {shop.logo ? (
              <img src={shop.logo} alt="Logo" className="w-14 h-14 object-contain rounded-lg" />
            ) : (
              <Store size={32} className="text-white" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-xl">{shop.name}</h2>
              {shop.verified && (
                <CheckCircle2 size={18} className="text-white/90" />
              )}
            </div>
            <p className="text-white/80 text-sm">{shop.description.substring(0, 80)}...</p>
          </div>
          <Button 
            className="bg-white/20 hover:bg-white/30 rounded-xl"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/boutique/${shop.slug}`);
              toast.success('Lien copié !');
            }}
          >
            <Share2 size={16} className="mr-2" />
            Partager
          </Button>
        </div>
      </div>

      {/* Informations générales */}
      <Section title="Informations générales" description="Les informations de base de ta boutique">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la boutique</label>
            <Input
              value={shop.name}
              onChange={(e) => setShop({ ...shop, name: e.target.value })}
              className="rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL de la boutique</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm">{window.location.origin}/boutique/</span>
              <Input
                value={shop.slug}
                onChange={(e) => setShop({ ...shop, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                className="rounded-xl flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={shop.description}
              onChange={(e) => setShop({ ...shop, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">{shop.description.length}/500 caractères</p>
          </div>
        </div>
      </Section>

      {/* Images */}
      <Section title="Images" description="Logo et bannière de ta boutique">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <div 
              onClick={() => handleImageUpload('logo')}
              className="aspect-square max-w-[200px] border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
            >
              {shop.logo ? (
                <img src={shop.logo} alt="Logo" className="w-full h-full object-contain rounded-xl" />
              ) : (
                <>
                  <Camera size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Ajouter un logo</span>
                  <span className="text-xs text-gray-400">PNG, JPG (200x200px)</span>
                </>
              )}
            </div>
          </div>

          {/* Banner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bannière</label>
            <div 
              onClick={() => handleImageUpload('banner')}
              className="aspect-video border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-500 hover:bg-orange-50 transition-colors"
            >
              {shop.banner ? (
                <img src={shop.banner} alt="Banner" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <>
                  <Image size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Ajouter une bannière</span>
                  <span className="text-xs text-gray-400">PNG, JPG (1200x400px)</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Couleur & Style */}
      <Section title="Couleur & Style" description="Personnalise les couleurs de ta boutique">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Couleur principale</label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={shop.primaryColor}
              onChange={(e) => setShop({ ...shop, primaryColor: e.target.value })}
              className="w-12 h-12 rounded-lg cursor-pointer border-0"
            />
            <Input
              value={shop.primaryColor}
              onChange={(e) => setShop({ ...shop, primaryColor: e.target.value })}
              className="w-32 rounded-xl font-mono"
            />
            <div className="flex gap-2">
              {['#F97316', '#3B82F6', '#22C55E', '#8B5CF6', '#EC4899'].map((color) => (
                <button
                  key={color}
                  onClick={() => setShop({ ...shop, primaryColor: color })}
                  className={`w-8 h-8 rounded-full border-2 ${shop.primaryColor === color ? 'border-gray-900' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Contact */}
      <Section title="Coordonnées" description="Tes informations de contact">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin size={14} className="inline mr-1" /> Adresse
            </label>
            <Input
              value={shop.address}
              onChange={(e) => setShop({ ...shop, address: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone size={14} className="inline mr-1" /> Téléphone
            </label>
            <Input
              value={shop.phone}
              onChange={(e) => setShop({ ...shop, phone: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail size={14} className="inline mr-1" /> Email
            </label>
            <Input
              value={shop.email}
              onChange={(e) => setShop({ ...shop, email: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Globe size={14} className="inline mr-1" /> Site web
            </label>
            <Input
              value={shop.website}
              onChange={(e) => setShop({ ...shop, website: e.target.value })}
              className="rounded-xl"
            />
          </div>
        </div>
      </Section>

      {/* Réseaux sociaux */}
      <Section title="Réseaux sociaux" description="Connecte tes comptes sociaux">
        <div className="space-y-4">
          {[
            { key: 'facebook', icon: Facebook, label: 'Facebook', color: '#1877F2' },
            { key: 'instagram', icon: Instagram, label: 'Instagram', color: '#E4405F' },
            { key: 'twitter', icon: Twitter, label: 'Twitter / X', color: '#1DA1F2' },
            { key: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: '#0A66C2' },
            { key: 'youtube', icon: Youtube, label: 'YouTube', color: '#FF0000' },
          ].map(({ key, icon: Icon, label, color }) => (
            <div key={key} className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${color}15` }}
              >
                <Icon size={20} style={{ color }} />
              </div>
              <Input
                value={shop.socials[key]}
                onChange={(e) => setShop({ 
                  ...shop, 
                  socials: { ...shop.socials, [key]: e.target.value }
                })}
                placeholder={`URL ${label}`}
                className="rounded-xl flex-1"
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Horaires */}
      <Section title="Horaires d'ouverture" description="Indique tes heures de disponibilité">
        <Input
          value={shop.openingHours}
          onChange={(e) => setShop({ ...shop, openingHours: e.target.value })}
          placeholder="Ex: Lun-Ven: 9h-18h, Sam: 9h-12h"
          className="rounded-xl"
        />
      </Section>
    </div>
  );
}
